import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import React, { useEffect, useRef, useState } from "react";
import { ListCard } from "../Cards/ListCard";
import { Icon } from "../Icon";
import { Portal } from "../Portal";
import styles from "./TimelinePanel.module.scss";
import { useTimelinePanelStore } from "./TimelinePanelStore";
import {
  TimelinePanelContentProps,
  TimelinePanelDrawerProps,
  TimelinePanelFooterProps,
  TimelinePanelHeaderProps,
  TimelinePanelMode,
  TimelinePanelSubComponents,
  TimelinePanelSubComponentsType,
  TimelinePanelTriggerProps,
} from "./types";

const cx = classNames.bind(styles);
const DRAWER_ANIMATION_DURATION_MS = 150;
const INITIAL_SHADOW_STYLE = { opacity: 0 };
const INITIAL_DRAWER_STYLE = { width: 0 };
const WIDTH = { sm: 320, md: 480, lg: 640 };
const CARDS_IN_LOADING_STATE = 6;

const getDrawerAnimationConfig = () => ({
  duration: DRAWER_ANIMATION_DURATION_MS,
});

/**
 * The trigger for the TimelinePanel that opens it.
 *  Default UI is provided but can be completely overriden.
 */
const TimelinePanelTrigger = ({
  children,
  label,
  targetDrawer,
}: TimelinePanelTriggerProps) => {
  const {
    isOpen,
    targetDrawer: currentDrawer,
    setIsOpen,
    setTargetDrawer,
  } = useTimelinePanelStore();

  const handleClick = () => {
    if (targetDrawer === currentDrawer) {
      setIsOpen(!isOpen);
    } else {
      setIsOpen(true);
    }
    setTargetDrawer(targetDrawer);
  };

  // When a new TimelinePanelTrigger first renders, close the current opened drawer.
  useEffect(() => {
    if (targetDrawer !== currentDrawer && isOpen) {
      setIsOpen(false);
    }
  }, []);

  if (!children || typeof children !== "function") {
    return (
      <div className={cx("trigger-container")}>
        <button
          disabled={isOpen}
          onClick={handleClick}
        >
          {label}
        </button>
      </div>
    );
  }

  return (
    <>
      {children({
        isOpen,
        setIsOpen,
        label,
        onClick: handleClick,
      })}
    </>
  );
};

/**
 * The drawer representing the TimelinePanel itself.
 * The header, content, and footer of the drawer can be overriden.
 */
const TimelinePanelDrawer = ({
  children,
  size = "sm",
  mode = TimelinePanelMode.OVERLAY,
  onClose: onCloseCallback,
  className,
  style,
  shouldRenderPortal = true,
  drawerKey,
  minimizedWidth,
  expandedWidth,
}: TimelinePanelDrawerProps) => {
  const DRAWER_WIDTH: number = expandedWidth ?? WIDTH[size];
  const isRailMode = mode === TimelinePanelMode.RAIL;

  const { isOpen, targetDrawer, setIsOpen, setDrawerWidth, setDrawerMode } =
    useTimelinePanelStore();

  const hasSyncedRailStateRef = useRef(false);

  const getTargetWidth = (open: boolean) => {
    if (open) {
      return DRAWER_WIDTH;
    }

    return isRailMode ? (minimizedWidth ?? 0) : 0;
  };

  const initialDrawerWidth =
    isRailMode && drawerKey === targetDrawer
      ? getTargetWidth(isOpen)
      : INITIAL_DRAWER_STYLE.width;

  const [isVisible, setIsVisible] = useState(
    isRailMode && drawerKey === targetDrawer
  );

  const [shadowAnimationStyle, shadowApi] = useSpring(() => ({
    config: getDrawerAnimationConfig(),
    ...INITIAL_SHADOW_STYLE,
  }));

  const [drawerAnimationStyle, drawerApi] = useSpring(() => ({
    config: getDrawerAnimationConfig(),
    width: initialDrawerWidth,
  }));

  const setDrawerAnimatedWidth = (
    open: boolean,
    { immediate = false }: { immediate?: boolean } = {}
  ) => {
    const width = getTargetWidth(open);

    drawerApi.start({
      width,
      immediate,
      config: getDrawerAnimationConfig(),
    });
    setDrawerWidth(width);
  };

  const startOpenAnimations = () => {
    setIsVisible(true);
    if (shouldRenderPortal) {
      shadowApi.start({
        opacity: 1,
        config: getDrawerAnimationConfig(),
      });
    }
    setDrawerAnimatedWidth(true);
  };

  const onClose = () => {
    if (isRailMode) {
      if (shouldRenderPortal) {
        shadowApi.start({
          ...INITIAL_SHADOW_STYLE,
          config: getDrawerAnimationConfig(),
        });
      }
      setDrawerAnimatedWidth(false);
      onCloseCallback?.();
      return;
    }

    if (shouldRenderPortal) {
      shadowApi.start({
        ...INITIAL_SHADOW_STYLE,
        config: getDrawerAnimationConfig(),
      });
    }
    drawerApi.start({
      ...INITIAL_DRAWER_STYLE,
      config: getDrawerAnimationConfig(),
    });

    onCloseCallback?.();

    setTimeout(() => {
      setIsVisible(false);
      setIsOpen(false);
    }, DRAWER_ANIMATION_DURATION_MS);
  };

  const renderDrawerSubComponent = (
    subComponentName: TimelinePanelSubComponentsType
  ) => {
    return (
      Array.isArray(children) &&
      children.map((child) => {
        if (
          React.isValidElement(child) &&
          typeof child.type !== "string" &&
          // @ts-expect-error: this property does exist because of React
          child.type.displayName === subComponentName
        ) {
          return child;
        }
        return null;
      })
    );
  };

  const renderDrawerContent = () => (
    <>
      {shouldRenderPortal && (
        <animated.div
          className={cx("shadow")}
          style={shadowAnimationStyle}
          onClick={onClose}
        />
      )}
      <animated.div
        className={cx("drawer", className, {
          collapsed: isRailMode && !isOpen,
        })}
        style={{ ...drawerAnimationStyle, ...style }}
        role="dialog"
        aria-expanded={isOpen}
      >
        {renderDrawerSubComponent(TimelinePanelSubComponents.HEADER)}
        {isOpen
          ? renderDrawerSubComponent(TimelinePanelSubComponents.CONTENT)
          : null}
        {isOpen
          ? renderDrawerSubComponent(TimelinePanelSubComponents.FOOTER)
          : null}
      </animated.div>
    </>
  );

  useEffect(() => {
    if (drawerKey === targetDrawer) {
      setDrawerMode(mode);

      if (isRailMode && !hasSyncedRailStateRef.current) {
        hasSyncedRailStateRef.current = true;
        setIsVisible(true);
        setDrawerAnimatedWidth(isOpen, { immediate: true });
        return;
      }

      if (isOpen) {
        startOpenAnimations();
      } else {
        onClose();
      }
    } else if (drawerKey === null) {
      onClose();
    } else if (isRailMode) {
      setIsVisible(false);
    }
  }, [isOpen, targetDrawer]);

  useEffect(() => {
    if (drawerKey === targetDrawer) {
      setDrawerWidth(getTargetWidth(isOpen));
    }
  }, [DRAWER_WIDTH, minimizedWidth, drawerKey, isOpen, targetDrawer]);

  const renderDrawerContainer = () => {
    const container = (
      <div
        className={cx(
          "container",
          shouldRenderPortal ? "with-portal" : "without-portal",
          {
            isOpen,
            isCollapsible: isRailMode,
          }
        )}
      >
        {renderDrawerContent()}
      </div>
    );

    if (shouldRenderPortal) {
      return <Portal>{container}</Portal>;
    }

    return container;
  };

  if (isRailMode) {
    if (drawerKey !== targetDrawer) {
      return null;
    }

    return renderDrawerContainer();
  }

  if (!isVisible || drawerKey !== targetDrawer) return null;

  return renderDrawerContainer();
};

type TimelinePanelHeaderActionProps = {
  headerActionClassName?: string;
};

const TimelinePanelHeaderAction = ({
  headerActionClassName,
}: TimelinePanelHeaderActionProps) => {
  const { isOpen, setIsOpen, mode } = useTimelinePanelStore();
  const isRailMode = mode === TimelinePanelMode.RAIL;

  if (isRailMode) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cx("headerActionButton", headerActionClassName)}
        aria-label={isOpen ? "Collapse" : "Expand"}
      >
        <Icon
          variant={isOpen ? "caret-left" : "caret-right"}
          size={24}
        />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsOpen(false)}
      className={cx("headerActionButton", headerActionClassName)}
      aria-label="Close"
    >
      <Icon
        variant="x"
        size={24}
      />
    </button>
  );
};

/**
 * The header of the TimelinePanel.
 */
const TimelinePanelHeader = ({
  title,
  className,
  titleSectionClassName,
  titleClassName,
  headerActionClassName,
  style,
}: TimelinePanelHeaderProps) => {
  const { drawerWidth } = useTimelinePanelStore();

  return (
    <div
      className={cx("header", className)}
      style={{ ...style, width: drawerWidth }}
    >
      <div className={cx("titleSection", titleSectionClassName)}>
        <h5 className={cx("title", titleClassName)}>{title}</h5>
      </div>
      <TimelinePanelHeaderAction
        headerActionClassName={headerActionClassName}
      />
    </div>
  );
};

TimelinePanelHeader.displayName = "TimelinePanelHeader";

/**
 * The content of the TimelinePanel.
 */
const TimelinePanelContent = ({
  children,
  isLoading = false,
  className,
  style,
  ...restProps
}: TimelinePanelContentProps) => {
  const { drawerWidth } = useTimelinePanelStore();
  return (
    <div
      className={cx("content", isLoading && "is-loading", className)}
      style={{ ...style, width: drawerWidth }}
      {...restProps}
    >
      {isLoading
        ? Array.from({ length: CARDS_IN_LOADING_STATE }, (_, index) => {
            return (
              <ListCard
                key={index}
                renderTitle={({ FieldSegment }) => (
                  <FieldSegment>Title</FieldSegment>
                )}
                renderDescription={({ FieldSegment }) => (
                  <FieldSegment>Description</FieldSegment>
                )}
                renderMeta={({ FieldSegment }) => (
                  <FieldSegment>Meta</FieldSegment>
                )}
                isLoading
                hasBorderBottom={index !== CARDS_IN_LOADING_STATE - 1}
              />
            );
          })
        : children}
    </div>
  );
};

TimelinePanelContent.displayName = "TimelinePanelContent";

/**
 * The footer of the TimelinePanel.
 */
const TimelinePanelFooter = ({
  children,
  className,
  style,
}: TimelinePanelFooterProps) => {
  const { drawerWidth } = useTimelinePanelStore();

  return (
    <div
      className={cx("footer", className)}
      style={{ ...style, width: drawerWidth }}
    >
      {children}
    </div>
  );
};

TimelinePanelFooter.displayName = "TimelinePanelFooter";

const Trigger = TimelinePanelTrigger;
const Drawer = TimelinePanelDrawer;
const Header = TimelinePanelHeader;
const Content = TimelinePanelContent;
const Footer = TimelinePanelFooter;

const TimelinePanel = {
  Trigger,
  Drawer,
  Header,
  Content,
  Footer,
};

export {
  TimelinePanel,
  TimelinePanelContent,
  TimelinePanelDrawer,
  TimelinePanelFooter,
  TimelinePanelHeader,
  TimelinePanelTrigger,
};
